import React, { useEffect, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { InfoOutlined } from "@mui/icons-material";
import { useHistory, useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import {
  InNetworkDisclaimer,
  UpfrontPaymentBigCost,
  UpfrontPaymentButtonRow,
  UpfrontPaymentDesktopContainer,
  UpfrontPaymentFieldRow,
  UpfrontPaymentFieldRowTitle,
  UpfrontPaymentFinancialAssistance,
  UpfrontPaymentFinancialAssistanceWrapper,
  UpfrontPaymentFinancialExplanation,
  UpfrontPaymentFinancialExplanationCrossOut,
  UpfrontPaymentInfoRow,
  UpfrontPaymentLink,
  UpfrontPaymentMainBanner,
  UpfrontPaymentMainBannerBottomRow,
  UpfrontPaymentMainBannerTopRow,
  UpfrontPaymentMainNoticeBanner,
  UpfrontPaymentMainNoticeRow,
  UpfrontPaymentPageLink,
  UpfrontPaymentPlanAccordionContainer,
  UpfrontPaymentSmallMainBanner,
  UpfrontPaymentYouHaveABill,
  UpfrontPaymentPhoneLinkStyle,
} from "./UpfrontPayment.styles";

import { DeviceType, useDeviceHook } from "../../utils/hooks/useDeviceHook";

import { GetTypographyContent } from "../shared/Typography/Typography";
import { RightArrowIcon } from "../../assets/iconComponents/RightArrow";
import { Button } from "../shared/Button/Button";
import { CentsConversion, PaymentPlanMinDollars, StepperIndex } from "./types";
import {
  BillPreview,
  getCentsToDollars,
  RemoveUpfrontPayment,
  Bill,
} from "./Services/UpfrontPaymentService";
import { WarningRounded } from "../../assets/iconComponents/WarningRounded";
import { InfoIcon } from "../../assets/iconComponents/InfoIcon";
import { UpfrontInsuranceUploadModal } from "./UpfrontPaymentInsuranceUploadModal";
import { NoticeIcon } from "../../assets/iconComponents/NoticeIcon";
import { EOBIcon } from "../../assets/iconComponents/EOBIcon";
import { PaidIcon } from "../../assets/iconComponents/PaidIcon";
import { GreenCheckIcon } from "../../assets/iconComponents/GreenCheck";
import { PaymentPlanIcon } from "../../assets/iconComponents/PaymentPlanIcon";
import { UpfrontPaymentPlanAccordion } from "./UpfrontPaymentPlanAccordion";
import { UpfrontPaymentReceiptDownload } from "./UpfrontPaymentReceiptDownload";
import { BabyPeekRadioButton } from "../BabyPeek/BabyPeekRadioButton";
import {
  BabyPeekPaymentPlanTitle,
  BabyPeekPSavedCardGridRow,
  BabyPeekStripeIconPointGridText,
  BabyPeekStripeIconPointViewSampleTax,
} from "../BabyPeek/BabyPeekStripe.styles";
import { getCardIcon } from "../BabyPeek/BabyPeekStripe";
import { getUserAuth } from "../../recoil/selectors";
import { failedPaymentBillPkState } from "../../recoil/atoms";
import { SavedCardType } from "../BabyPeek/Services/BabyPeekService";
import { SendActionType } from "../shared/Services/SharedServices";
import { UpfrontPaymentOA100 } from "./UpfrontPaymentOA100";
import { dateFormatter } from "../../utils/helpers";
import UpfrontPaymentPhoneLink from "./UpfrontPaymentPhoneLink";
import { InsuranceUploadType } from "../Insurance/types";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import { AnalyticsEventNames } from "../../utils/analyticsEvents";
import { StatusDetails } from "./StatusDetails";
import { FASupportingDocumentUploadModal } from "./FASupportingDocumentUploadModal";
import { BillingInfoTitle, BillingInfoTooltip } from "./BillingInfoTooltip";

const getUpperMessage = (
  upfrontBill: Bill,
  setPaymentOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setInsurance: () => void,
  deviceType: DeviceType,
  t: TFunction<"translation">,
) => {
  if (
    (upfrontBill.portal_bill_status === "paid_in_full" ||
      upfrontBill.portal_bill_status === "refunded" ||
      upfrontBill.portal_bill_status === "in_progress") &&
    upfrontBill.oa_100_billing_status !== "outstanding"
  ) {
    return (
      <UpfrontPaymentYouHaveABill>
        <UpfrontPaymentMainNoticeBanner deviceType={deviceType}>
          <UpfrontPaymentMainBanner>
            <UpfrontPaymentMainBannerTopRow>
              <NoticeIcon /> {t("upfrontPayment.receipt.notice")}
            </UpfrontPaymentMainBannerTopRow>
            <UpfrontPaymentMainNoticeRow>
              {t("upfrontPayment.main.eobNoticeBody")}
              <span>{t("upfrontPayment.main.eobNoticeAction")}</span>
            </UpfrontPaymentMainNoticeRow>
          </UpfrontPaymentMainBanner>
          <EOBIcon />
        </UpfrontPaymentMainNoticeBanner>
      </UpfrontPaymentYouHaveABill>
    );
  }
  if (
    upfrontBill.portal_bill_status === "secondary_ins_provided" ||
    upfrontBill.portal_bill_status === "primary_ins_provided" ||
    upfrontBill.portal_bill_status === "medicaid_or_hmo_ins_provided"
  ) {
    return (
      <UpfrontPaymentYouHaveABill>
        <UpfrontPaymentMainBanner>
          <UpfrontPaymentMainBannerTopRow>
            <InfoOutlined /> {t("upfrontPayment.main.pendingReview")}
          </UpfrontPaymentMainBannerTopRow>
          <UpfrontPaymentMainBannerBottomRow>
            {t("upfrontPayment.main.pendingReviewBody")}
          </UpfrontPaymentMainBannerBottomRow>
        </UpfrontPaymentMainBanner>
      </UpfrontPaymentYouHaveABill>
    );
  }
  if (upfrontBill.is_cash_pay && upfrontBill.accept_insurance) {
    return (
      <UpfrontPaymentYouHaveABill>
        <UpfrontPaymentMainBanner>
          <UpfrontPaymentMainBannerTopRow>
            <InfoOutlined /> {t("upfrontPayment.main.insuranceHelpTitle")}
          </UpfrontPaymentMainBannerTopRow>

          <UpfrontPaymentMainBannerBottomRow>
            <>
              {`If so, please `}
              <UpfrontPaymentLink onClick={() => setInsurance()}>
                {t("upfrontPayment.main.addInsurance")}
              </UpfrontPaymentLink>
              {` or if you need assistance, call us at `}
              <UpfrontPaymentPhoneLink />
              {`.`}{" "}
            </>
          </UpfrontPaymentMainBannerBottomRow>
        </UpfrontPaymentMainBanner>
      </UpfrontPaymentYouHaveABill>
    );
  }
  if (upfrontBill.bill_type === "in_network" && !upfrontBill.is_final) {
    return (
      <UpfrontPaymentYouHaveABill>
        <UpfrontPaymentMainBanner>
          <UpfrontPaymentMainBannerTopRow>
            <InfoOutlined /> Your claim is being applied to your in-network
            benefits with Cigna
          </UpfrontPaymentMainBannerTopRow>

          <UpfrontPaymentMainBannerBottomRow>
            <>
              {`Your balance due will apply to your in-network benefits. This amount is based on a co-insurance, co-pay, or annual in-network deductible on your plan. If you have questions, please contact us at `}
              <UpfrontPaymentPhoneLink />
              {`.`}{" "}
            </>
          </UpfrontPaymentMainBannerBottomRow>
        </UpfrontPaymentMainBanner>
      </UpfrontPaymentYouHaveABill>
    );
  }
  if (
    upfrontBill.has_submitted_financial_assistance &&
    !upfrontBill.is_qualified_for_financial_assistance &&
    upfrontBill.portal_bill_status === "outstanding"
  ) {
    return (
      <UpfrontPaymentYouHaveABill>
        <UpfrontPaymentMainBanner>
          <UpfrontPaymentMainBannerTopRow>
            <WarningRounded />{" "}
            {`It doesn't look like your financial information
            qualifies for an income based reduction `}
          </UpfrontPaymentMainBannerTopRow>
          <UpfrontPaymentMainBannerBottomRow>
            <>
              {`However, you may still qualify for other discounts. Please
            contact us at `}
              <UpfrontPaymentPhoneLink />
              {` to discuss your options with one of our team members.`}{" "}
            </>
          </UpfrontPaymentMainBannerBottomRow>
        </UpfrontPaymentMainBanner>
      </UpfrontPaymentYouHaveABill>
    );
  }
  if (upfrontBill.bill_type === "in_network" && upfrontBill.is_final) {
    return (
      <UpfrontPaymentYouHaveABill>
        <UpfrontPaymentMainBanner>
          <UpfrontPaymentMainBannerTopRow>
            <WarningRounded />
            <div>
              Your claim was approved and applied to your{" "}
              <span style={{ fontWeight: "bold", display: "inline" }}>
                {`in-network `}
              </span>
              benefits.
            </div>
          </UpfrontPaymentMainBannerTopRow>

          <UpfrontPaymentMainBannerBottomRow>
            This may have gone towards satisfying an annual in-network
            deductible on your plan, which may reduce your cost share on future
            medical claims.* View the itemized bill below for more detail. If
            you have questions, please contact us at <UpfrontPaymentPhoneLink />
            .
          </UpfrontPaymentMainBannerBottomRow>
        </UpfrontPaymentMainBanner>
      </UpfrontPaymentYouHaveABill>
    );
  }
  if (upfrontBill.show_financial_assistance) {
    return (
      <UpfrontPaymentYouHaveABill>
        <UpfrontPaymentMainBanner>
          <UpfrontPaymentMainBannerTopRow>
            <InfoOutlined /> Looking for Assistance with Your Bill?
          </UpfrontPaymentMainBannerTopRow>

          <UpfrontPaymentMainBannerBottomRow>
            <>
              {`If you need assistance with your balance, please click `}
              <UpfrontPaymentPhoneLinkStyle
                onClick={() => setPaymentOpen(true)}
                onKeyDown={() => {}}
                tabIndex={0}
                role="button"
                style={{ cursor: "pointer", textDecoration: "underline=" }}
              >
                Here
              </UpfrontPaymentPhoneLinkStyle>
              {` or if you need assistance, call us at `}
              <UpfrontPaymentPhoneLink />
              {`.`}{" "}
            </>
          </UpfrontPaymentMainBannerBottomRow>
        </UpfrontPaymentMainBanner>
      </UpfrontPaymentYouHaveABill>
    );
  }
};

export const UpfrontPaymentMain: React.FC<{
  setStep: React.Dispatch<React.SetStateAction<StepperIndex>>;
  setPaymentOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setStripeOpen: (open: boolean) => Promise<void>;
  setUpfrontBill: React.Dispatch<React.SetStateAction<Bill | undefined>>;
  upfrontBill: Bill;
  paymentSubmitted: boolean;
  setHomeScreen: () => void;
  savedPayments?: SavedCardType[];
  currentPaymentMethodForPaymentPlan?: SavedCardType;
  setSavedPayments: React.Dispatch<
    React.SetStateAction<SavedCardType[] | undefined>
  >;
  setCurrentPaymentMethodForPaymentPlan: React.Dispatch<
    React.SetStateAction<SavedCardType | undefined>
  >;
  setUsedSavePayment: React.Dispatch<React.SetStateAction<string | undefined>>;
  useSavedPayment?: string;
  useNewCard: boolean;
  setUseNewCard: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({
  setStep,
  setPaymentOpen,
  setUpfrontBill,
  setStripeOpen,
  paymentSubmitted,
  upfrontBill,
  setHomeScreen,
  savedPayments,
  currentPaymentMethodForPaymentPlan,
  setSavedPayments,
  setCurrentPaymentMethodForPaymentPlan,
  setUsedSavePayment,
  setUseNewCard,
  useNewCard,
  useSavedPayment,
}) => {
  const { deviceType } = useDeviceHook();
  const { t } = useTranslation();
  const [openInsuranceUpload, setOpenInsuranceUpload] =
    useState<boolean>(false);
  const [openFASupportingDocumentUpload, setOpenFASupportingDocumentUpload] =
    useState<boolean>(false);
  const [receiptDownloadOpen, setReceiptDownloadOpen] = useState(false);
  const { billingPK } = useParams<{ billingPK: string }>();
  const auth = useRecoilValue(getUserAuth);
  const failedPaymentBillPk = useRecoilValue(failedPaymentBillPkState);
  const isPastDue =
    new Date(upfrontBill.due_date).setHours(0, 0, 0, 0) <
    new Date().setHours(0, 0, 0, 0);
  const isFinancialAssistanceQualifiedBill =
    (upfrontBill.is_qualified_for_financial_assistance &&
      upfrontBill.portal_bill_status === "outstanding") ||
    upfrontBill.portal_bill_status === "fa_fully_covered" ||
    upfrontBill.portal_bill_status === "has_medicaid";
  const showFASupportingDocumentationLink =
    upfrontBill.has_submitted_financial_assistance;

  useEffect(() => {
    if (auth?.access_token) {
      TrackAnalyticsEvent(
        AnalyticsEventNames.impression,
        auth?.access_token,
        auth?.user_id,
        {
          impression_type: "page_view",
          page_name: "UpfrontBillSinglePage",
          bill_pk: billingPK,
        },
      );
    }
  }, []);

  return (
    <>
      <UpfrontPaymentDesktopContainer deviceType={deviceType}>
        <UpfrontInsuranceUploadModal
          insuranceType={
            upfrontBill.is_cash_pay
              ? InsuranceUploadType.primary
              : InsuranceUploadType.secondary
          }
          insuranceUploadModalOpen={openInsuranceUpload}
          setInsuranceUploadModalOpen={setOpenInsuranceUpload}
          setUpfrontBill={setUpfrontBill}
          setHomeScreen={setHomeScreen}
        />
        <UpfrontPaymentReceiptDownload
          receiptDownloadOpen={receiptDownloadOpen}
          setReceiptDownloadOpen={setReceiptDownloadOpen}
          upfrontBill={upfrontBill}
        />
        <FASupportingDocumentUploadModal
          open={openFASupportingDocumentUpload}
          setOpen={setOpenFASupportingDocumentUpload}
          setUpfrontBill={setUpfrontBill}
        />
        {upfrontBill && (
          <>
            {isFinancialAssistanceQualifiedBill ? (
              <div>
                {" "}
                <UpfrontPaymentInfoRow>
                  <UpfrontPaymentFieldRow>
                    <UpfrontPaymentFieldRowTitle>
                      UPDATED AMOUNT OWED
                    </UpfrontPaymentFieldRowTitle>
                    <UpfrontPaymentFinancialExplanationCrossOut>
                      <UpfrontPaymentBigCost>
                        {getCentsToDollars(upfrontBill.balance)}
                      </UpfrontPaymentBigCost>
                      <div className="cross-out">
                        {" "}
                        {getCentsToDollars(
                          upfrontBill.total_insurance_allowed_amount -
                            upfrontBill.total_insurance_paid_amount,
                        )}
                      </div>
                    </UpfrontPaymentFinancialExplanationCrossOut>
                  </UpfrontPaymentFieldRow>
                </UpfrontPaymentInfoRow>
                <UpfrontPaymentFinancialExplanation>
                  <GreenCheckIcon />
                  <div>
                    {`Thank you for submitting your information! You have qualified
                  for our ${
                    upfrontBill.portal_bill_status === "has_medicaid"
                      ? "medicaid"
                      : "financial assistance"
                  } program and we've reduced your
                  balance. `}
                  </div>
                </UpfrontPaymentFinancialExplanation>
              </div>
            ) : (
              getUpperMessage(
                upfrontBill,
                setPaymentOpen,
                () => setOpenInsuranceUpload(true),
                deviceType,
                t,
              )
            )}
            <UpfrontPaymentInfoRow>
              {!(
                upfrontBill.portal_bill_status === "paid_in_full" ||
                upfrontBill.portal_bill_status === "refunded"
              ) &&
                !isFinancialAssistanceQualifiedBill && (
                  <UpfrontPaymentFieldRow>
                    <BillingInfoTitle>
                      <span className="billing-info-title-label">
                        {t("upfrontPayment.main.amountOwed")}{" "}
                      </span>
                      <BillingInfoTooltip
                        label={t("upfrontPayment.main.amountOwed")}
                        content={t("upfrontPayment.main.amountOwedTooltip")}
                        icon={<InfoIcon />}
                      />
                    </BillingInfoTitle>
                    {GetTypographyContent({
                      content: getCentsToDollars(upfrontBill?.balance),
                      size: "medium",
                      textColor: "black",
                    })}
                  </UpfrontPaymentFieldRow>
                )}
              <UpfrontPaymentFieldRow>
                <UpfrontPaymentFieldRowTitle>
                  {t("upfrontPayment.multipleBills.paymentStatus")}
                </UpfrontPaymentFieldRowTitle>
                <StatusDetails
                  billPreview={upfrontBill}
                  paymentSubmitted={paymentSubmitted}
                  isPastDue={isPastDue}
                  hasFailedPayment={failedPaymentBillPk === upfrontBill.pk}
                />
              </UpfrontPaymentFieldRow>
            </UpfrontPaymentInfoRow>
            <UpfrontPaymentInfoRow>
              <UpfrontPaymentFieldRow>
                <UpfrontPaymentFieldRowTitle>
                  {t("upfrontPayment.receipt.dateOfService")}
                </UpfrontPaymentFieldRowTitle>
                {GetTypographyContent({
                  content: dateFormatter(upfrontBill?.date_of_service),
                  size: "medium",
                  textColor: "black",
                })}
              </UpfrontPaymentFieldRow>
              <UpfrontPaymentFieldRow>
                <UpfrontPaymentFieldRowTitle>
                  {t("upfrontPayment.receipt.provider")}
                </UpfrontPaymentFieldRowTitle>
                {GetTypographyContent({
                  content: upfrontBill?.provider_name,
                  size: "medium",
                  textColor: "black",
                })}
              </UpfrontPaymentFieldRow>
            </UpfrontPaymentInfoRow>
            {upfrontBill?.is_cash_pay && upfrontBill.accept_insurance && (
              <UpfrontPaymentInfoRow>
                <UpfrontPaymentFieldRow>
                  <UpfrontPaymentFieldRowTitle>
                    {t("upfrontPayment.main.billingMethod")}
                  </UpfrontPaymentFieldRowTitle>
                  {GetTypographyContent({
                    content: t("upfrontPayment.main.selfPay"),
                    size: "medium",
                    textColor: "black",
                  })}
                </UpfrontPaymentFieldRow>
                <UpfrontPaymentFieldRow>
                  <UpfrontPaymentFieldRowTitle>
                    {t("upfrontPayment.main.insurance")}
                  </UpfrontPaymentFieldRowTitle>
                  {upfrontBill.portal_bill_status === "primary_ins_provided"
                    ? GetTypographyContent({
                        content: t("upfrontPayment.main.pendingReview"),
                        size: "medium",
                        textColor: "black",
                        isItalic: true,
                      })
                    : upfrontBill.portal_bill_status === "outstanding" &&
                      !upfrontBill.has_submitted_financial_assistance && (
                        <UpfrontPaymentLink
                          onClick={() => setOpenInsuranceUpload(true)}
                        >
                          {t("upfrontPayment.actions.add")}
                        </UpfrontPaymentLink>
                      )}
                </UpfrontPaymentFieldRow>
              </UpfrontPaymentInfoRow>
            )}
            {!upfrontBill?.is_cash_pay && upfrontBill.accept_insurance && (
              <UpfrontPaymentInfoRow>
                <UpfrontPaymentFieldRow>
                  <UpfrontPaymentFieldRowTitle>
                    {t("upfrontPayment.main.primaryInsurance")}
                  </UpfrontPaymentFieldRowTitle>
                  {upfrontBill.portal_bill_status ===
                  "medicaid_or_hmo_ins_provided"
                    ? GetTypographyContent({
                        content: t("upfrontPayment.main.pendingReview"),
                        size: "medium",
                        textColor: "black",
                        isItalic: true,
                      })
                    : GetTypographyContent({
                        content: upfrontBill?.insurance_name,
                        size: "medium",
                        textColor: "black",
                      })}
                </UpfrontPaymentFieldRow>
                <UpfrontPaymentFieldRow>
                  <BillingInfoTitle>
                    <span className="billing-info-title-label">
                      {deviceType === "mobile"
                        ? t("upfrontPayment.main.secondaryInsuranceShort")
                        : t("upfrontPayment.main.secondaryInsurance")}
                    </span>
                    {upfrontBill.portal_bill_status === "outstanding" &&
                      !upfrontBill.has_submitted_financial_assistance && (
                        <BillingInfoTooltip
                          label={t("upfrontPayment.main.secondaryInsurance")}
                          content={t(
                            "upfrontPayment.main.secondaryInsuranceTooltip",
                          )}
                          icon={<InfoIcon />}
                        />
                      )}
                  </BillingInfoTitle>
                  {upfrontBill.portal_bill_status === "secondary_ins_provided"
                    ? GetTypographyContent({
                        content: t("upfrontPayment.main.pendingReview"),
                        size: "medium",
                        textColor: "black",
                        isItalic: true,
                      })
                    : upfrontBill.portal_bill_status === "outstanding" &&
                      !upfrontBill.has_submitted_financial_assistance &&
                      upfrontBill.accept_insurance && (
                        <UpfrontPaymentLink
                          onClick={() => setOpenInsuranceUpload(true)}
                        >
                          {t("upfrontPayment.actions.add")}
                        </UpfrontPaymentLink>
                      )}
                </UpfrontPaymentFieldRow>
              </UpfrontPaymentInfoRow>
            )}
            <UpfrontPaymentInfoRow>
              {!upfrontBill.is_cash_pay && upfrontBill.accept_insurance && (
                <UpfrontPaymentPageLink
                  onClick={() => setStep(StepperIndex.Insurance)}
                >
                  <div>{t("upfrontPayment.main.insurance")}</div>
                  <RightArrowIcon />
                </UpfrontPaymentPageLink>
              )}
              {upfrontBill.portal_bill_status !== "fa_fully_covered" && (
                <UpfrontPaymentPageLink
                  onClick={() => setStep(StepperIndex.ItemizedBill)}
                >
                  <div>{t("upfrontPayment.main.itemizedBill")}</div>
                  <RightArrowIcon />
                </UpfrontPaymentPageLink>
              )}
            </UpfrontPaymentInfoRow>
            <UpfrontPaymentInfoRow>
              {!(upfrontBill.portal_bill_status === "secondary_ins_provided") &&
                paymentSubmitted && (
                  <UpfrontPaymentPageLink
                    onClick={() =>
                      upfrontBill.is_payment_plan_enabled
                        ? setReceiptDownloadOpen(true)
                        : setStep(StepperIndex.Receipt)
                    }
                  >
                    <div>{t("upfrontPayment.main.receipt")}</div>
                    <RightArrowIcon />
                  </UpfrontPaymentPageLink>
                )}
            </UpfrontPaymentInfoRow>
            {Boolean(savedPayments?.length) &&
              upfrontBill.portal_bill_status === "outstanding" && (
                <div>
                  <hr />
                  <BabyPeekPaymentPlanTitle>
                    {t("upfrontPayment.main.payment")}
                  </BabyPeekPaymentPlanTitle>
                  {savedPayments
                    ?.filter((saved) => saved.is_chargeable)
                    .map((saved) => (
                      <BabyPeekPSavedCardGridRow key={saved.payment_method_id}>
                        <BabyPeekRadioButton
                          clicked={useSavedPayment === saved.payment_method_id}
                          setClick={() => {
                            setUsedSavePayment(saved.payment_method_id);
                            setUseNewCard(false);
                          }}
                        />
                        <BabyPeekStripeIconPointGridText
                          onClick={() => {
                            setUsedSavePayment(saved.payment_method_id);
                            setUseNewCard(false);
                          }}
                        >
                          <img src={getCardIcon(saved.card_brand)} alt="card" />
                          {`•••• ${saved.last_four_digits}`}
                        </BabyPeekStripeIconPointGridText>
                        {!saved.is_used_for_payment_plan && (
                          <BabyPeekStripeIconPointViewSampleTax
                            deviceType={deviceType}
                            onClick={async () => {
                              const removeResponse = await RemoveUpfrontPayment(
                                billingPK,
                                saved.payment_method_id,
                                auth?.access_token,
                              );

                              const savedCards = await removeResponse.json();
                              setSavedPayments(savedCards.payment_methods);
                            }}
                          >
                            {t("upfrontPayment.actions.remove")}
                          </BabyPeekStripeIconPointViewSampleTax>
                        )}
                      </BabyPeekPSavedCardGridRow>
                    ))}
                  <BabyPeekPSavedCardGridRow>
                    <BabyPeekRadioButton
                      clicked={useNewCard}
                      setClick={() => {
                        setUseNewCard(true);
                        setUsedSavePayment(undefined);
                      }}
                    />
                    <BabyPeekStripeIconPointGridText
                      onClick={() => {
                        setUseNewCard(true);
                        setUsedSavePayment(undefined);
                      }}
                    >
                      {t("upfrontPayment.actions.payWithNewCard")}
                    </BabyPeekStripeIconPointGridText>
                  </BabyPeekPSavedCardGridRow>
                </div>
              )}
            {upfrontBill.portal_bill_status === "outstanding" && (
              <>
                <UpfrontPaymentButtonRow>
                  <Button
                    type="submit"
                    label={t("upfrontPayment.actions.payAmount", {
                      amount: getCentsToDollars(upfrontBill?.balance),
                    })}
                    size="medium"
                    onClick={() => setStripeOpen(true)}
                    backgroundColor="greenDark"
                  />
                </UpfrontPaymentButtonRow>
                {upfrontBill.balance / CentsConversion >
                  PaymentPlanMinDollars && (
                  <UpfrontPaymentButtonRow bottomRow>
                    <Button
                      type="submit"
                      label={t("upfrontPayment.actions.buildPaymentPlan")}
                      size="medium"
                      reverse
                      onClick={() => {
                        setStripeOpen(false);
                        setStep(StepperIndex.PaymentPlan);
                        SendActionType(
                          "PreviewPaymentPlan",
                          upfrontBill.req_identifier,
                          auth?.access_token,
                        );
                      }}
                      backgroundColor="greenDark"
                    />
                  </UpfrontPaymentButtonRow>
                )}

                {upfrontBill.bill_type === "in_network" &&
                  upfrontBill.is_final && (
                    <InNetworkDisclaimer>
                      *Individual policies and circumstances vary greatly. Refer
                      to your individual health insurance policy for coverage
                      details or contact your insurance company for details on
                      future expected out-of-pocket costs.
                    </InNetworkDisclaimer>
                  )}

                {upfrontBill.show_financial_assistance && (
                  <UpfrontPaymentFinancialAssistanceWrapper
                    onClick={() => setPaymentOpen(true)}
                  >
                    <UpfrontPaymentFinancialAssistance>
                      {t("upfrontPayment.actions.applyForFinancialAssistance")}
                    </UpfrontPaymentFinancialAssistance>
                  </UpfrontPaymentFinancialAssistanceWrapper>
                )}
                {upfrontBill.bill_type === "out_of_network" &&
                  !upfrontBill.show_financial_assistance &&
                  upfrontBill?.oa100_balance === 0 && (
                    <>
                      <br />
                      <UpfrontPaymentYouHaveABill>
                        <UpfrontPaymentSmallMainBanner>
                          <UpfrontPaymentMainBannerTopRow>
                            <InfoOutlined /> Looking for Assistance with Your
                            Bill?
                          </UpfrontPaymentMainBannerTopRow>

                          <UpfrontPaymentMainBannerBottomRow>
                            <>
                              {`Call us at `}
                              <UpfrontPaymentPhoneLink />
                              {`.`}{" "}
                            </>
                          </UpfrontPaymentMainBannerBottomRow>
                        </UpfrontPaymentSmallMainBanner>
                      </UpfrontPaymentYouHaveABill>
                    </>
                  )}
              </>
            )}
            {showFASupportingDocumentationLink && (
              <UpfrontPaymentFinancialAssistanceWrapper>
                <UpfrontPaymentLink
                  onClick={() => setOpenFASupportingDocumentUpload(true)}
                >
                  Upload supporting documentation
                </UpfrontPaymentLink>
              </UpfrontPaymentFinancialAssistanceWrapper>
            )}
          </>
        )}
      </UpfrontPaymentDesktopContainer>
      {upfrontBill?.is_payment_plan_enabled && (
        <UpfrontPaymentPlanAccordionContainer deviceType={deviceType}>
          <UpfrontPaymentPlanAccordion
            upfrontBill={upfrontBill}
            currentPaymentMethodForPaymentPlan={
              currentPaymentMethodForPaymentPlan
            }
            setSavedPayments={setSavedPayments}
            setCurrentPaymentMethodForPaymentPlan={
              setCurrentPaymentMethodForPaymentPlan
            }
            setUpfrontBill={setUpfrontBill}
          />
        </UpfrontPaymentPlanAccordionContainer>
      )}

      {upfrontBill?.oa_100_billing_status !== "not_available" && (
        <UpfrontPaymentOA100
          upfrontBill={upfrontBill}
          setUpfrontBill={setUpfrontBill}
        />
      )}
    </>
  );
};
